import { FC, ReactNode, useRef } from "react";
import { Box, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { useDrawingArea } from "@mui/x-charts/hooks";
import { PieChart } from "@mui/x-charts/PieChart";
import { useTranslation } from "react-i18next";
import { formatCompactNumber } from "../../../../common/utils";
import PaperCard from "../../../widgets/PaperCard";
import PaperCardHeader from "../../../widgets/PaperCard/PaperCardHeader";

export interface DataChart {
	id: string | number;
	percent: number;
	value: number;
	label: string;
	icon: ReactNode;
}

type Props = {
	totalEmissions?: number;
	isLoading: boolean;
	onReload?: VoidFunction;
	title?: string;
	keyEmissions?: string;
	dataChart?: DataChart[];
	colors: string[];
	elevation?: number;
};

const PieChartDistributionType: FC<Props> = ({
	totalEmissions,
	isLoading,
	onReload,
	title,
	keyEmissions,
	dataChart,
	colors,
	elevation = 3,
}) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const { t } = useTranslation("common");
	const theme = useTheme();

	function PieCenterLabel({ value, label }: { value: number; label: string }) {
		const { width, height, left, top } = useDrawingArea();

		const centerX = left + width / 2;
		const centerY = top + height / 2;

		const getEmissionMeasure = (keyEmissions: "co2Ttw" | "co2Wtw"): string => {
			const map = {
				co2Ttw: "tCO2eTtW",
				co2Wtw: "tCO2eWtW",
			} as const;

			return map[keyEmissions];
		};

		return (
			<text x={centerX} y={centerY} textAnchor="middle" dominantBaseline="central">
				<Typography
					x={centerX}
					dy="-0.1em"
					variant="h4"
					component="tspan"
					fontWeight="700"
					sx={{ fill: (theme) => theme.palette.primary.main }}
				>
					{formatCompactNumber(value, 0, 1)}
				</Typography>

				<Typography
					x={centerX}
					dy="2em"
					variant="caption"
					component="tspan"
					sx={{ fill: (theme) => theme.palette.primary.main }}
				>
					{getEmissionMeasure(label as "co2Ttw" | "co2Wtw")}
				</Typography>
			</text>
		);
	}

	const hasData = Boolean(totalEmissions && totalEmissions !== 0);

	const pieData = hasData
		? (dataChart ?? [])
		: [
				{
					id: "no-data",
					value: 1,
					label: t("no data"),
					percent: 100,
					icon: null,
				},
			];

	const pieColors = hasData ? colors : [theme.palette.grey[300]];

	if (isLoading) return <Skeleton variant="rounded" height="100%" width="100%" />;

	return (
		<PaperCard
			elevation={elevation}
			ref={containerRef}
			header={
				onReload && (
					<PaperCardHeader
						containerRef={containerRef}
						title={title ?? ""}
						isLoading={isLoading}
						onReload={onReload}
					/>
				)
			}
			content={
				<Stack
					direction={{ xs: "row", md: "column" }}
					max-height={{ xs: 250, md: "auto" }}
					width={"100%"}
					height={"100%"}
					justifyContent={"space-between"}
					alignItems={"center"}
				>
					<Box
						flex={{ xs: 1, md: "auto" }}
						sx={{
							height: 220,
							flexGrow: 1,
							width: "100%",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<PieChart
							width={220}
							height={220}
							colors={pieColors}
							slotProps={
								!hasData
									? {
											tooltip: {
												trigger: "none",
											},
										}
									: undefined
							}
							series={[
								{
									type: "pie",
									data: pieData,
									innerRadius: 78,
									outerRadius: 92,
									paddingAngle: hasData ? 4 : 0,
									cornerRadius: hasData ? 8 : 0,

									...(hasData && {
										highlightScope: {
											fade: "global",
											highlight: "item",
										},
										faded: {
											additionalRadius: -5,
											color: "gray",
										},
									}),
								},
							]}
							sx={{
								"& .MuiChartsLegend-root": {
									display: "none",
								},
							}}
							margin={{ top: 10, bottom: 10, left: 10, right: 10 }}
						>
							<PieCenterLabel value={totalEmissions || 0} label={keyEmissions ?? ""} />
						</PieChart>
					</Box>

					{hasData ? (
						<Stack
							flex={1}
							direction={{ xs: "column", md: "row" }}
							spacing={2}
							justifyContent={"center"}
							height={{ xs: "auto", md: 60 }}
							alignItems={"center"}
							flexWrap="wrap"
							gap={2}
						>
							{dataChart?.map((type) => (
								<Stack
									key={type.label}
									direction={"row"}
									spacing={0.5}
									gap={0.5}
									alignItems={"center"}
									width={{ xs: 160, md: "auto" }}
								>
									{type.icon}
									<Typography variant="subtitle2">{type.percent}%</Typography>
									<Typography variant="caption">{type.label}</Typography>
								</Stack>
							))}
						</Stack>
					) : (
						<Typography variant="body2">{t("no data")}</Typography>
					)}
				</Stack>
			}
			sxContent={{ paddingRight: 0, paddingLeft: 0 }}
		/>
	);
};

export default PieChartDistributionType;
